import { cn } from "@/lib/utils";
import { AlertCircle, Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

type ActionState = "idle" | "loading" | "success" | "error";

interface ActionButtonProps {
	onClick: () => void;
	disabled?: boolean;
	isPending?: boolean;
	success?: boolean;
	error?: boolean;
	children: React.ReactNode;
	className?: string;
	variant?: "primary" | "secondary" | "destructive";
	layout?: "row" | "stack";
	loadingLabel?: string;
	errorLabel?: string;
}

/** Unified Deposit / Withdraw / Pay Loan CTA with morphing states. */
export function ActionButton({
	onClick,
	disabled,
	isPending,
	success: successProp,
	error: errorProp,
	children,
	className,
	variant = "primary",
	layout = "row",
	loadingLabel = "Processing…",
	errorLabel = "Retry",
}: ActionButtonProps) {
	const [localSuccess, setLocalSuccess] = useState(false);
	const [localError, setLocalError] = useState(false);

	useEffect(() => {
		if (successProp) {
			setLocalSuccess(true);
			const t = setTimeout(() => setLocalSuccess(false), 2000);
			return () => clearTimeout(t);
		}
	}, [successProp]);

	useEffect(() => {
		if (errorProp) {
			setLocalError(true);
			const t = setTimeout(() => setLocalError(false), 2500);
			return () => clearTimeout(t);
		}
	}, [errorProp]);

	const state: ActionState = isPending
		? "loading"
		: localSuccess
			? "success"
			: localError
				? "error"
				: "idle";

	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled || isPending}
			className={cn(
				"relative inline-flex min-h-touch w-full items-center justify-center gap-2 rounded-xl px-3 text-sm font-semibold disabled:pointer-events-none disabled:opacity-50",
				layout === "stack" && "min-h-[4.25rem] flex-col px-2 py-2.5",
				variant === "primary" && "btn-primary",
				variant === "secondary" && "btn-secondary",
				variant === "destructive" &&
					"border border-danger/40 bg-danger/10 text-danger",
				state === "success" && "!bg-primary !text-primary-foreground",
				state === "error" && "!border-danger/50 !bg-danger/15 !text-danger",
				className,
			)}
		>
			{state === "loading" && (
				<span className="flex items-center justify-center gap-2">
					<Loader2 className="size-5 shrink-0 animate-spin" />
					{loadingLabel}
				</span>
			)}
			{state === "success" && (
				<span className="flex items-center justify-center gap-2">
					<Check className="size-5 shrink-0" />
					Done!
				</span>
			)}
			{state === "error" && (
				<span className="flex items-center justify-center gap-2">
					<AlertCircle className="size-5 shrink-0" />
					{errorLabel}
				</span>
			)}
			{state === "idle" && (
				<span
					className={cn(
						"flex items-center justify-center gap-2",
						layout === "stack" && "w-full flex-col gap-1 text-center",
					)}
				>
					{children}
				</span>
			)}
		</button>
	);
}
